/**
 * Diálogo «Agrega tu KPI»: lista, crea, edita y borra las fórmulas personales del usuario,
 * con validación en vivo y vista previa sobre los datos filtrados del momento.
 */
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ink, studio } from '../admin/studioUi';
import { KPI_FIELDS, KPI_FORMATS, evaluateKpi, formatKpi, prepareKpi, validateKpiName } from './customKpis';

const EMPTY = { id: '', name: '', formula: '', format: 'number' };
const GROUP_LABELS = { posts: 'Publicaciones', followers: 'Seguidores' };

/** Dónde aparece un KPI según los campos que usa. */
export function kpiScopeLabel(kpi) {
    if (kpi.groups.length > 1) return 'Resumen';
    return kpi.groups[0] === 'followers' ? 'Resumen y Seguidores' : 'Resumen y Publicaciones';
}

const KpiManager = ({ open, kpis, onChange, onClose, previewRows }) => {
    const [draft, setDraft] = useState(null);
    const [touched, setTouched] = useState(false);

    const compiled = useMemo(() => {
        if (!draft) return { kpi: null, error: '' };
        try {
            return { kpi: prepareKpi(draft), error: '' };
        } catch (err) {
            return { kpi: null, error: err.message };
        }
    }, [draft]);
    const nameError = draft
        ? validateKpiName(draft.name, kpis.filter(k => k.id !== draft.id).map(k => k.name))
        : '';
    const preview = compiled.kpi ? evaluateKpi(compiled.kpi, previewRows) : null;

    if (!open) return null;

    const close = () => { setDraft(null); setTouched(false); onClose(); };
    const save = (event) => {
        event.preventDefault();
        setTouched(true);
        if (nameError || !compiled.kpi) return;
        const item = { ...draft, name: draft.name.trim(), id: draft.id || `kpi-${Date.now()}` };
        onChange(draft.id ? kpis.map(k => (k.id === draft.id ? item : k)) : [...kpis, item]);
        setDraft(null);
        setTouched(false);
    };
    const insert = (name) => {
        const token = /\s|\(/.test(name) ? `[${name}]` : name;
        setDraft(d => ({ ...d, formula: `${d.formula}${d.formula && !/[\s(+\-*/]$/.test(d.formula) ? ' + ' : ''}${token}` }));
    };

    return createPortal(
        <div style={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
            <div style={styles.dialog} role="dialog" aria-modal="true" aria-label="Agrega tu KPI">
                <div style={styles.head}>
                    <div>
                        <p style={studio.kicker}>Solo para ti</p>
                        <h3 style={styles.title}>Tus KPIs</h3>
                        <p style={studio.hint}>
                            Fórmulas personales que se calculan con los filtros activos. Se guardan en este navegador y
                            nadie más las ve.
                        </p>
                    </div>
                    <button type="button" onClick={close} style={studio.iconBtn} aria-label="Cerrar">×</button>
                </div>

                {!draft && (
                    <>
                        <div style={styles.list}>
                            {kpis.map((kpi) => (
                                <div key={kpi.id} style={styles.item}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={styles.itemName}>{kpi.name}</div>
                                        <code style={styles.code}>{kpi.formula}</code>
                                        <div style={studio.hint}>{kpi.groups ? kpiScopeLabel(kpi) : ''}</div>
                                    </div>
                                    <div style={styles.itemActions}>
                                        <button type="button" style={studio.ghostBtn} onClick={() => setDraft({ ...EMPTY, ...kpi })}>Editar</button>
                                        <button type="button" style={studio.dangerBtn} onClick={() => onChange(kpis.filter(k => k.id !== kpi.id))}>Quitar</button>
                                    </div>
                                </div>
                            ))}
                            {!kpis.length && <p style={studio.hint}>Aún no tienes KPIs. Ejemplo: <code style={styles.code}>clics + saves</code>.</p>}
                        </div>
                        <div style={styles.foot}>
                            <button type="button" style={studio.primaryBtn} onClick={() => setDraft({ ...EMPTY })}>Nuevo KPI</button>
                        </div>
                    </>
                )}

                {draft && (
                    <form onSubmit={save} style={styles.form}>
                        <label>
                            <span style={studio.fieldLabel}>Nombre</span>
                            <input
                                autoFocus
                                value={draft.name}
                                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                                placeholder="interacciones_mias"
                                style={studio.control}
                            />
                            {touched && nameError && <span style={styles.fieldError}>{nameError}</span>}
                        </label>
                        <label>
                            <span style={studio.fieldLabel}>Fórmula</span>
                            <input
                                value={draft.formula}
                                onChange={e => setDraft(d => ({ ...d, formula: e.target.value }))}
                                placeholder="clics + saves"
                                style={{ ...studio.control, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                                spellCheck={false}
                            />
                            {draft.formula && compiled.error && <span style={styles.fieldError}>{compiled.error}</span>}
                        </label>
                        <div>
                            <span style={studio.fieldLabel}>Campos (clic para agregar) · operaciones + − × ÷ y paréntesis</span>
                            {['posts', 'followers'].map(group => (
                                <div key={group} style={styles.chipGroup}>
                                    <span style={styles.chipGroupLabel}>{GROUP_LABELS[group]}</span>
                                    {KPI_FIELDS.filter(f => f.group === group).map(field => (
                                        <button key={field.name} type="button" style={styles.chip} onClick={() => insert(field.name)}>{field.name}</button>
                                    ))}
                                </div>
                            ))}
                        </div>
                        <label>
                            <span style={studio.fieldLabel}>Formato</span>
                            <select value={draft.format} onChange={e => setDraft(d => ({ ...d, format: e.target.value }))} style={studio.control}>
                                {KPI_FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                            </select>
                        </label>
                        <div style={styles.preview}>
                            <span style={studio.statLabel}>Vista previa con tus filtros</span>
                            <span style={styles.previewValue}>{compiled.kpi ? formatKpi(compiled.kpi, preview) : '—'}</span>
                            {compiled.kpi && <span style={studio.hint}>Aparece en: {kpiScopeLabel(compiled.kpi)}</span>}
                        </div>
                        <div style={styles.foot}>
                            <button type="button" style={studio.ghostBtn} onClick={() => { setDraft(null); setTouched(false); }}>Cancelar</button>
                            <button type="submit" style={studio.primaryBtn} disabled={!compiled.kpi}>Guardar KPI</button>
                        </div>
                    </form>
                )}
            </div>
        </div>,
        document.body
    );
};

const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.38)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
    },
    dialog: {
        width: 'min(620px, 100%)',
        maxHeight: 'calc(100vh - 32px)',
        overflowY: 'auto',
        backgroundColor: ink.surface,
        borderRadius: 16,
        boxShadow: '0 20px 50px rgba(28, 29, 33, 0.25)',
        padding: '22px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        boxSizing: 'border-box',
    },
    head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
    title: { margin: '4px 0 6px', fontSize: '1.2rem', fontWeight: 600, color: ink.text },
    list: { display: 'flex', flexDirection: 'column', gap: 10 },
    item: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '12px 14px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
    },
    itemName: { fontWeight: 600, color: ink.text },
    itemActions: { display: 'flex', gap: 8 },
    code: {
        display: 'inline-block',
        margin: '4px 0',
        padding: '2px 8px',
        borderRadius: 6,
        backgroundColor: ink.wash,
        fontSize: '0.82rem',
        color: ink.accentText,
        wordBreak: 'break-word',
    },
    form: { display: 'flex', flexDirection: 'column', gap: 14 },
    fieldError: { display: 'block', marginTop: 6, color: ink.danger, fontSize: '0.8rem' },
    chipGroup: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 8 },
    chipGroupLabel: { fontSize: '0.72rem', color: ink.faint, fontWeight: 700, marginRight: 4, minWidth: 92 },
    chip: {
        padding: '4px 10px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        fontSize: '0.78rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
    },
    preview: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '12px 14px',
        borderRadius: 12,
        backgroundColor: ink.accentSoft,
    },
    previewValue: { fontSize: '1.4rem', fontWeight: 600, color: ink.accentText },
    foot: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
};

export default KpiManager;
